import { Helmet, HelmetProvider } from 'react-helmet-async';
import type { SEOProps } from '../../types';
import { businessInfo } from '../../utils/businessInfo';

const defaultSEO: Required<SEOProps> = {
    title: 'Swag Shots - Create. Connect. Earn. | Short Video & Live Streaming App',
    description: 'Swag Shots is a short-video and live-streaming app. Create reels, go live, connect with friends, and explore creator features where available.',
    keywords: 'Swag Shots, SwagShots, short videos, live streaming, create reels, social media app, video creator, content creation, mobile app, viral videos, influencer',
    image: 'https://www.swagshots.com/assets/logo-og.webp',
    url: 'https://www.swagshots.com',
    author: 'Swag Shots',
    type: 'website'
};

interface SEOWrapperProps {
    children: React.ReactNode;
}

export const SEOWrapper: React.FC<SEOWrapperProps> = ({ children }) => {
    return (
        <HelmetProvider>
            {children}
        </HelmetProvider>
    );
};

export const SEO: React.FC<SEOProps> = ({
    title,
    description,
    keywords,
    image,
    url,
    author,
    type
}) => {
    const seoTitle = title ? `${title} | Swag Shots` : defaultSEO.title;
    const seoDescription = description || defaultSEO.description;
    const seoKeywords = keywords || defaultSEO.keywords;
    const seoImage = image || defaultSEO.image;
    const seoUrl = url || defaultSEO.url;
    const seoAuthor = author || defaultSEO.author;
    const seoType = type || defaultSEO.type;

    // Generate structured data based on page type
    const getStructuredData = () => {
        const baseData = {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Swag Shots",
            "description": seoDescription,
            "url": seoUrl,
            "applicationCategory": "SocialNetworkingApplication",
            "operatingSystem": "Android",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
            },
            "author": {
                "@type": "Organization",
                "name": businessInfo.operatorName
            },
            "publisher": {
                "@type": "Organization",
                "name": businessInfo.operatorName,
                "taxID": businessInfo.gstin
            }
        };

        // Add Organization data for homepage
        if (seoUrl === defaultSEO.url || seoUrl === 'https://www.swagshots.com/') {
            return [
                baseData,
                {
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": businessInfo.operatorName,
                    "alternateName": "Swag Shots",
                    "url": "https://www.swagshots.com",
                    "logo": "https://www.swagshots.com/assets/logo-512.webp",
                    "description": seoDescription,
                    "taxID": businessInfo.gstin,
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "Bagdia, Unnamed Road, P.S. Dhantala",
                        "addressLocality": "Daluabari",
                        "addressRegion": "West Bengal",
                        "postalCode": "741504",
                        "addressCountry": "IN"
                    },
                    "sameAs": [
                        "https://www.facebook.com/swagshots",
                        "https://www.twitter.com/swagshots",
                        "https://www.instagram.com/swagshots"
                    ],
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "email": businessInfo.email,
                        "contactType": "Customer Support"
                    }
                }
            ];
        }

        return baseData;
    };

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{seoTitle}</title>
            <meta name="description" content={seoDescription} />
            <meta name="keywords" content={seoKeywords} />
            <meta name="author" content={seoAuthor} />
            <meta name="robots" content="index, follow" />
            <meta name="language" content="en" />
            <meta name="revisit-after" content="7 days" />
            <meta name="distribution" content="global" />
            <meta name="rating" content="general" />
            <link rel="canonical" href={seoUrl} />

            {/* Open Graph Meta Tags */}
            <meta property="og:title" content={seoTitle} />
            <meta property="og:description" content={seoDescription} />
            <meta property="og:image" content={seoImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:url" content={seoUrl} />
            <meta property="og:type" content={seoType} />
            <meta property="og:site_name" content="Swag Shots" />
            <meta property="og:locale" content="en_US" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seoTitle} />
            <meta name="twitter:description" content={seoDescription} />
            <meta name="twitter:image" content={seoImage} />
            <meta name="twitter:site" content="@SwagShots" />
            <meta name="twitter:creator" content="@SwagShots" />

            {/* Favicons */}
            <link rel="icon" type="image/x-icon" href="/assets/favicon.ico" />
            <link rel="apple-touch-icon" sizes="180x180" href="/assets/apple-touch-icon.png" />
            <link rel="icon" type="image/png" sizes="32x32" href="/assets/favicon-32x32.png" />
            <link rel="icon" type="image/png" sizes="16x16" href="/assets/favicon-16x16.png" />
            <link rel="manifest" href="/site.webmanifest" />

            {/* Additional Meta Tags */}
            <meta name="theme-color" content="#c2410c" />
            <meta name="msapplication-TileColor" content="#c2410c" />
            <meta name="msapplication-config" content="/browserconfig.xml" />

            {/* Schema.org structured data */}
            <script type="application/ld+json">
                {JSON.stringify(getStructuredData())}
            </script>
        </Helmet>
    );
};
